import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import { useDailyMenuApi } from "../api/DailyMenuApi";
import { useFontenehus } from "../context/FontenehusContext";
import { handleApiError } from "../utils/handleApiError";

const MainPage: React.FC = () => {
  const navigate = useNavigate();
  usePageTitle("FonteneLunsj – Hovedside");

  const today = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }, []);

  const currentYear = today.getFullYear();
  const { refreshTrigger } = useFontenehus();
  const [year, setYear] = useState(currentYear);
  const [monthIndex, setMonthIndex] = useState(today.getMonth());
  const [showFullYear, setShowFullYear] = useState(false);
  const [quarterIndex, setQuarterIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [recipeStatuses, setRecipeStatuses] = useState<Record<string, boolean>>(
    {},
  );
  const [loading, setLoading] = useState(false);
  const { getDailyMenuStatusesInRange } = useDailyMenuApi();
  const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
    today.getDate(),
  ).padStart(2, "0")}`;

  const months = [
    "Januar",
    "Februar",
    "Mars",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Desember",
  ];
  const weekdays = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 600);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleDayClick = (datestr: string) => {
    navigate("/dagsmeny", { state: { selectedDate: new Date(datestr) } });
  };

  const getDaysInMonth = (y: number, m: number) =>
    new Date(y, m + 1, 0).getDate();

  const getFirstWeekdayOffset = (y: number, m: number) => {
    const firstDay = new Date(y, m, 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1;
  };
  // === Fetch recipe/day statuses ===
  useEffect(() => {
    const fetchStatuses = async () => {
      setLoading(true);
      try {
        let from: string;
        let to: string;

        if (!showFullYear) {
          // Month view
          const first = `${year}-${String(monthIndex + 1).padStart(2, "0")}-01`;
          const last = `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(
            getDaysInMonth(year, monthIndex),
          ).padStart(2, "0")}`;
          from = first;
          to = last;
        } else if (isMobile) {
          // Quarter view
          const startMonth = quarterIndex * 3;
          const endMonth = startMonth + 2;
          const fromDay = `${year}-${String(startMonth + 1).padStart(2, "0")}-01`;
          const lastDay = getDaysInMonth(year, endMonth);
          const toDay = `${year}-${String(endMonth + 1).padStart(2, "0")}-${String(
            lastDay,
          ).padStart(2, "0")}`;
          from = fromDay;
          to = toDay;
        } else {
          // Full year view
          from = `${year}-01-01`;
          to = `${year}-12-31`;
        }

        // ✅ Fetch from API
        const response = await getDailyMenuStatusesInRange(from, to);
        const data = response.data.data;

        // Convert to dictionary
        const dict: Record<string, boolean> = {};
        for (const d of data) dict[d.date] = d.hasRecipe;

        setRecipeStatuses(dict);
      } catch (err) {
        handleApiError(err, "En feil oppsto ved lasting av dagsmeny-status.");
        setRecipeStatuses({});
      } finally {
        setLoading(false);
      }
    };

    fetchStatuses();
  }, [year, monthIndex, quarterIndex, showFullYear, isMobile, refreshTrigger]);
  // === Helpers ===
  const hasRecipe = (dateStr: string) => recipeStatuses[dateStr] === true;

  const isWithinThisWeek = (dateStr: string) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    const check = new Date(y, m - 1, d);
    const restOfWeek = new Date(today);
    const daysToFriday = 5 - new Date().getDay();

    restOfWeek.setDate(today.getDate() + daysToFriday);
    return check >= today && check <= restOfWeek;
  };

  const dayColorClasses = (dateStr: string): string => {
    const [y, m, d] = dateStr.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const isToday = dateStr === todayString;
    const has = hasRecipe(dateStr);
    const withinWeek = isWithinThisWeek(dateStr);

    // ✅ TODAY (stronger highlight)
    if (isToday) {
      if (has) {
        // Today has recipe → brighter green
        return "bg-green-300 text-green-900 ring-4 ring-green-500 shadow-md";
      } else {
        // Today has NO recipe
        return isWeekend
          ? "bg-orange-400 text-white ring-4 ring-orange-500 shadow-md" // Weekend → orange highlight
          : "bg-red-300 text-red-900 ring-4 ring-red-500 shadow-md"; // Weekday → red highlight
      }
    }

    // ✅ Past date with recipe → green
    if (has && date < today) {
      return "bg-green-100 text-green-900 ring-2 ring-green-300";
    }

    // ✅ Future date with recipe → blue
    if (has && date > today) {
      return "bg-blue-100 text-blue-900 ring-2 ring-blue-300";
    }

    // ✅ No recipe and within next 7 days → red (weekdays only)
    if (!has && withinWeek && !isWeekend) {
      return "bg-red-100 text-red-900 ring-2 ring-red-300";
    }

    // ✅ Default
    return "bg-white hover:bg-orangeHover hover:text-white";
  };

  const renderDayButton = (dateStr: string, label: number) => {
    const classes = dayColorClasses(dateStr);
    return (
      <button
        key={dateStr}
        onClick={() => handleDayClick(dateStr)}
        className={`relative py-1 rounded-md text-sm font-medium transition ${classes}`}
        title={dateStr}
      >
        {label}
      </button>
    );
  };

  // === Month navigation ===
  const handlePrevMonth = () => {
    if (monthIndex === 0) {
      setMonthIndex(11);
      setYear((y) => y - 1);
    } else setMonthIndex((m) => m - 1);
  };
  const handleNextMonth = () => {
    if (monthIndex === 11) {
      setMonthIndex(0);
      setYear((y) => y + 1);
    } else setMonthIndex((m) => m + 1);
  };

  // === Quarter navigation ===
  const handlePrevQuarter = () => {
    if (quarterIndex === 0) {
      setQuarterIndex(3);
      setYear((y) => y - 1);
    } else setQuarterIndex((q) => q - 1);
  };
  const handleNextQuarter = () => {
    if (quarterIndex === 3) {
      setQuarterIndex(0);
      setYear((y) => y + 1);
    } else setQuarterIndex((q) => q + 1);
  };

  const currentQuarterMonths = months.slice(
    quarterIndex * 3,
    quarterIndex * 3 + 3,
  );

  // === RENDER ===
  return (
    <div className="fixed inset-0 flex flex-col bg-orange-50">
      <main className="flex-1 overflow-y-auto pt-[80px] pb-[70px] flex flex-col items-center text-textDark">
        <header className="text-center mt-2 mb-4">
          <h2 className="text-2xl font-bold text-orangeBrand">{year}</h2>
          <p className="text-textDark/70">
            {loading
              ? "Laster dagsmenyer..."
              : "Velg en dato for å se eller anmelde oppskrifter."}
          </p>
        </header>

        {/* === FULL YEAR VIEW (desktop) === */}
        {!isMobile && showFullYear && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 px-4 w-full max-w-6xl">
            {months.map((month, idx) => {
              const daysInMonth = getDaysInMonth(year, idx);
              const offset = getFirstWeekdayOffset(year, idx);
              const daysArray = Array(offset)
                .fill(null)
                .concat(Array.from({ length: daysInMonth }, (_, i) => i + 1));

              return (
                <div
                  key={month}
                  className="bg-white rounded-lg shadow p-4 text-center"
                >
                  <h3 className="text-lg font-semibold text-orangeBrand mb-2">
                    {month}
                  </h3>
                  <div className="grid grid-cols-7 text-xs text-gray-500 font-semibold mb-1">
                    {weekdays.map((d) => (
                      <div key={d}>{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {daysArray.map((day, dayIndex) => {
                      if (day === null)
                        return <div key={`empty-${dayIndex}`} />;
                      const dateStr = `${year}-${String(idx + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                      return renderDayButton(dateStr, day as number);
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* === QUARTER VIEW (mobile) === */}
        {isMobile && showFullYear && (
          <div className="flex flex-col items-center w-full max-w-md gap-4">
            <div className="flex justify-between w-full items-center">
              <button
                onClick={handlePrevQuarter}
                className="text-orangeBrand font-semibold hover:text-orangeHover"
              >
                ‹ Forrige
              </button>
              <h3 className="text-lg font-semibold text-orangeBrand">
                {year} – Kvartal {quarterIndex + 1}
              </h3>
              <button
                onClick={handleNextQuarter}
                className="text-orangeBrand font-semibold hover:text-orangeHover"
              >
                Neste ›
              </button>
            </div>

            <div className="flex flex-col gap-5 w-full">
              {currentQuarterMonths.map((month, i) => {
                const idx = quarterIndex * 3 + i;
                const daysInMonth = getDaysInMonth(year, idx);
                const offset = getFirstWeekdayOffset(year, idx);
                const daysArray = Array(offset)
                  .fill(null)
                  .concat(Array.from({ length: daysInMonth }, (_, i) => i + 1));

                return (
                  <div
                    key={month}
                    className="bg-white rounded-lg shadow p-3 text-center"
                  >
                    <h3 className="text-lg font-semibold text-orangeBrand mb-2">
                      {month}
                    </h3>
                    <div className="grid grid-cols-7 text-xs text-gray-500 font-semibold mb-1">
                      {weekdays.map((d) => (
                        <div key={d}>{d}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {daysArray.map((day, dayIndex) => {
                        if (day === null)
                          return <div key={`empty-${dayIndex}`} />;
                        const dateStr = `${year}-${String(idx + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                        return renderDayButton(dateStr, day as number);
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* === MONTH VIEW === */}
        {!showFullYear && (
          <div className="bg-white rounded-lg shadow p-5 w-full max-w-md text-center">
            <div className="flex justify-between items-center mb-3">
              <button
                onClick={handlePrevMonth}
                className="text-orangeBrand font-semibold hover:text-orangeHover"
              >
                ← Forrige
              </button>
              <h2 className="text-xl font-semibold text-orangeBrand">
                {months[monthIndex]}
              </h2>
              <button
                onClick={handleNextMonth}
                className="text-orangeBrand font-semibold hover:text-orangeHover"
              >
                Neste →
              </button>
            </div>

            <div className="grid grid-cols-7 text-xs text-gray-500 font-semibold mb-1">
              {weekdays.map((d) => (
                <div key={d}>{d}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {Array(getFirstWeekdayOffset(year, monthIndex))
                .fill(null)
                .map((_, idx) => (
                  <div key={`empty-${idx}`} />
                ))}
              {Array.from(
                { length: getDaysInMonth(year, monthIndex) },
                (_, i) => {
                  const day = i + 1;
                  const dateStr = `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  return renderDayButton(dateStr, day);
                },
              )}
            </div>
          </div>
        )}

        {/* === TOGGLE BUTTON === */}
        <div className="mt-6 mb-6">
          <button
            className="bg-orangeBrand text-white font-semibold px-4 py-2 rounded-md hover:bg-orangeHover transition"
            onClick={() => {
              if (!showFullYear) {
                const currentMonth = today.getMonth();
                const currentQuarter = Math.floor(currentMonth / 3);
                setQuarterIndex(currentQuarter);
                setYear(currentYear);
              }
              setShowFullYear(!showFullYear);
            }}
          >
            {showFullYear
              ? "Vis bare denne måneden"
              : isMobile
                ? "Vis kvartal"
                : "Vis hele året"}
          </button>
        </div>
      </main>
    </div>
  );
};

export default MainPage;
